import { useState, useRef, useEffect } from "react";
import { Modal } from ".";
import Alert from "../alert/Alert";
import { X, Lock, KeyRound } from "lucide-react";
import EmailOtpModal from "./EmailOtpModal";
import ResetPinModal from "./ResetPinModal";
import { autoClearError } from "../../../utils/helpers";

interface EnterPinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPinVerified: (pin: string) => Promise<boolean>;
  title?: string;
  description?: string;
  showForgotPin?: boolean;
}

const EnterPinModal = ({
  isOpen,
  onClose,
  onPinVerified,
  title = "Enter PIN",
  description = "Enter your 4-digit PIN to continue",
  showForgotPin = true,
}: EnterPinModalProps) => {
  const [pin, setPin] = useState<string[]>(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      resetPin();
      setError("");
    }
  }, [isOpen]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    
    // Auto-focus next input
    if (value && index < 3) {
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus();
      }, 10);
    }
    
    // If all digits entered, validate automatically
    if (newPin.every(digit => digit !== "") && index === 3) {
      handleSubmit(newPin.join(""));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (!pin[index] && index > 0) {
        // Move to previous input if current is empty
        setTimeout(() => {
          inputRefs.current[index - 1]?.focus();
        }, 10);
      } else if (pin[index]) {
        // Clear current input
        const newPin = [...pin];
        newPin[index] = "";
        setPin(newPin);
      }
    }
  };

  const handleSubmit = async (enteredPin?: string) => {
    const pinToVerify = enteredPin || pin.join("");
    
    if (pinToVerify.length !== 4) {
      setError("Please enter a complete 4-digit PIN");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const isValid = await onPinVerified(pinToVerify);
      if (isValid) {
        onClose();
      } else {
        setError("Invalid PIN. Please try again.");
        autoClearError(setError);
        resetPin();
      }
    } catch (err) {
      setError("Failed to verify PIN. Please try again.");
      resetPin();
    } finally {
      setLoading(false);
    }
  };

  const resetPin = () => {
    setPin(["", "", "", ""]);
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
  };

  const handleClose = () => {
    resetPin();
    setError("");
    onClose();
  };

  const handleForgotPin = () => {
    setShowEmailModal(true);
  };

  const handleEmailSuccess = () => {
    setShowEmailModal(false);
    setShowResetModal(true);
  };

  const handleResetSuccess = () => {
    setShowResetModal(false);
    handleClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        className="max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900"
      >
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
                <Lock className="h-5 w-5 text-primary dark:text-white/90" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  {title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {description}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
              type="button"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6">
            <Alert variant="error" title="Error" message={error} />
          </div>
        )}

        {/* PIN Inputs */}
        <div className="mb-8">
          <div className="mb-4 flex justify-center gap-3">
            {[0, 1, 2, 3].map((index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="password"
                maxLength={1}
                value={pin[index]}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="h-14 w-14 rounded-lg border-2 border-gray-200 bg-gray-50 text-center text-2xl font-bold tracking-widest text-gray-800 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                disabled={loading}
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            ))}
          </div>

          <div className="text-center text-sm text-gray-500 dark:text-gray-400">
            Enter your 4-digit security PIN
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => handleSubmit()}
            disabled={loading || pin.join("").length !== 4}
            className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Verifying..." : "Confirm PIN"}
          </button>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={resetPin}
              className="text-sm text-gray-800 hover:text-gray-500 dark:text-white/90 dark:hover:text-gray-300"
              disabled={loading}
            >
              Clear PIN
            </button>

            {showForgotPin && (
              <button
                type="button"
                onClick={handleForgotPin}
                className="flex items-center gap-1 text-sm  text-gray-800 hover:text-gray-500 dark:text-white/90 dark:hover:text-gray-300"
                disabled={loading}
              >
                <KeyRound className="h-3 w-3" />
                Forgot PIN?
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* Forgot PIN Flow Modals */}
      <EmailOtpModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={handleEmailSuccess}
      />

      <ResetPinModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onSuccess={handleResetSuccess}
      />
    </>
  );
};

export default EnterPinModal;